import type { GroupChannel } from "@/crossPlatform/Sendbird";
import useChatChannelContext from "@/hooks/useChatChannels";
import { useAppUserSelector } from "@/store/reducers/appUser";
import ChannelInformationCard from "@/ui/components/messages/ChannelInformationCard";
import { Text } from "@/ui/core";
import { VStack } from "@/ui/layout";
import { ColorToken } from "@/ui/theme/tokens";
import {
  getChannelDisplayName,
  getInformationalModalGroupChannelDescription,
  isIndividualChannel,
} from "@/utils/groupChannel.util";
import {
  getProviderNames,
  sendbirdMemberToUserAvatar,
} from "@/utils/messages.util";

type ChannelType = "admissions" | "group" | "individual";

interface ChannelSectionInfoProps {
  description: string;
  id: string;
  name: string;
  type: ChannelType;
}

const SectionInfo: Record<ChannelType, ChannelSectionInfoProps> = {
  admissions: {
    description:
      "Recommended for working with your admissions team for enrolling in Equip treatment",
    id: "admissions-channel",
    name: "Admissions channel",
    type: "admissions",
  },
  group: {
    description:
      "Recommended for treatment or medical-related questions (such as prescriptions, appointments) or providing a group update to the entire provider team",
    id: "group-channels",
    name: "Treatment group channels",
    type: "group",
  },
  individual: {
    description:
      "Recommended for private conversations with each person on your provider team",
    id: "direct-message-channels",
    name: "Treatment direct message channels",
    type: "individual",
  },
};

interface MessagesInformationSection {
  channels: GroupChannel[];
  sectionInfo: ChannelSectionInfoProps;
}

const ChannelInformationSection = ({
  channels,
  sectionInfo,
}: MessagesInformationSection) => {
  const { selectedPatientName } = useChatChannelContext();
  const { isPatient: isAppUserPatient } = useAppUserSelector();

  return (
    <VStack gap={12}>
      <VStack gap={6}>
        <Text color={ColorToken.NeutralPrimary} testID="" variant="h5">
          {sectionInfo.name}
        </Text>

        <Text color={ColorToken.NeutralSecondary} testID="">
          {sectionInfo.description}
        </Text>
      </VStack>

      {channels.map((channel) => {
        const getChannelDescription = () => {
          if (sectionInfo.type === "group") {
            return getInformationalModalGroupChannelDescription({
              channelUrl: channel.url,
              isAppUserPatient,
              selectedPatientName,
            });
          }

          if (sectionInfo.type === "individual") {
            return getProviderNames(channel.members);
          }

          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- linting rule seems to be failing without reason here
          if (sectionInfo.type === "admissions") {
            return "Admissions";
          }

          // eslint-disable-next-line sonarjs/no-redundant-jump -- to enforce tsconfig's noImplicitReturn
          return;
        };

        const channelDescription = getChannelDescription();
        const channelDisplayName = getChannelDisplayName({
          channel,
          isAppUserPatient,
          selectedPatientName,
        });

        return (
          <ChannelInformationCard
            channelName={channelDisplayName}
            channelUrl={channel.url}
            description={channelDescription}
            isGroupChannel={!isIndividualChannel(channel.url)}
            key={channel.url}
            members={channel.members.map(sendbirdMemberToUserAvatar)}
          />
        );
      })}
    </VStack>
  );
};

export type { MessagesInformationSection };
export { SectionInfo };
export default ChannelInformationSection;
